import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";

import { VendorGroupService } from "../../../services/api/vendor-group.service";
import { ConsumerActions } from "../../actions/consumer.actions";
import { VendorGroupsState } from "../../models/consumer/vendor-groups-state";
import { FailState } from "../../models/fail-state";
import { AppState } from "../../reducers";
import { ConsumerSelectors } from "../../selectors/consumer.selectors";

@Injectable()
export class VendorGroupsEffects {
	get = createEffect(() =>
		this.actions.pipe(
			ofType(ConsumerActions.vendorGroups.get),
			withLatestFrom(this.store.select(ConsumerSelectors.vendorGroups)),
			switchMap(([action, state]) =>
				this.canUseStoreValue(state)
					? of(ConsumerActions.vendorGroups.getSuccess({
							vendorGroups: state.vendorGroups,
							loading: false,
							hasLoaded: true
						}))
					: of(ConsumerActions.vendorGroups.getApi())
			)
		)
	);

	getApi = createEffect(() =>
		this.actions.pipe(
			ofType(ConsumerActions.vendorGroups.getApi),
			switchMap((action) => {
				return this.vendorGroupService.getVendorGroups().pipe(
					map((vendorGroups) =>
						ConsumerActions.vendorGroups.getSuccess({
							vendorGroups,
							loading: false,
							hasLoaded: true
						})
					),
					catchError((error) =>
						of(ConsumerActions.vendorGroups.getFail(new FailState(error)))
					)
				);
			})
		)
	);

	create = createEffect(() =>
		this.actions.pipe(
			ofType(ConsumerActions.vendorGroups.create),
			switchMap((action) =>
				this.vendorGroupService.createVendorGroup(action.request).pipe(
					map((vendorGroup) => ConsumerActions.vendorGroups.createSuccess(vendorGroup)),
					catchError((error) =>
						of(ConsumerActions.vendorGroups.createFail(new FailState(error)))
					)
				)
			)
		)
	);

	update = createEffect(() =>
		this.actions.pipe(
			ofType(ConsumerActions.vendorGroups.update),
			switchMap((action) =>
				this.vendorGroupService.updateVendorGroup(action.request).pipe(
					map((vendorGroup) => ConsumerActions.vendorGroups.updateSuccess(vendorGroup)),
					catchError((error) =>
						of(ConsumerActions.vendorGroups.updateFail(new FailState(error)))
					)
				)
			)
		)
	);

	constructor(
		private actions: Actions,
		private vendorGroupService: VendorGroupService,
		private store: Store<AppState>
	) {}

	private canUseStoreValue(state: VendorGroupsState): boolean {
		return (
			state !== undefined &&
			!_.has(state, "error") &&
			!state.loading &&
			state.hasLoaded === true &&
			_.has(state, "vendorGroups") &&
			state.vendorGroups !== undefined
		);
	}
}
